<template>
  <standard-layout title="Account">
    <v-container class="pa-6">
      <v-row class="mb-6">
        <v-col cols="12">
          <v-text-field
            class="mt-2"
            variant="underlined"
            label="What's your name?"
            autocomplete="name"
            spellcheck="false"
            :model-value="$store.user.playerName"
            @update:model-value="$store.user.update({ playerName: $event })"
          />
        </v-col>
      </v-row>

      <v-row class="mb-6">
        <v-col cols="12">
          <div class="text-headline-small mb-2">Singles</div>
          <v-switch
            label="Are you ok with playing singles and Ironman matches?"
            :model-value="$store.user.singles"
            @update:model-value="$store.user.immediateUpdate({ singles: $event })"
            color="primary"
            inset
          />
        </v-col>
      </v-row>

      <v-row class="mt-6">
        <v-col cols="12">
          <div class="text-headline-small mb-2">Your rating</div>
          <div class="text-headline-large font-weight-bold text-primary">{{ $store.user.scorecard?.scaledDisplayRating || '' }}</div>
        </v-col>
      </v-row>

      <v-row v-if="chartData" class="mt-10">
        <v-col cols="12">
          <div class="text-body-1 font-weight-medium text-medium-emphasis mb-2">History</div>
          <v-progress-linear v-if="chartLoading" indeterminate />
          <div v-else class="mt-6" style="height: 400px; margin-bottom: 125px">
              <LineChart ref="chartComponent" :data="chartData" :options="chartOptions" />
            </div>
        </v-col>
      </v-row>
    </v-container>
  </standard-layout>
</template>

<script>
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
} from "chart.js";
import "chartjs-adapter-date-fns";
import { generateShades, buildRatingDataPoints, chartClickMixin, baseChartOptions } from "@/utils/chart-utils";
import { db, dbFns } from "@/db";
import { createSession } from "@/models/session";
import StandardLayout from "@/components/standard-layout.vue";
import { subMonths } from "date-fns";

ChartJS.register(LinearScale, PointElement, LineElement, Title, Tooltip, Legend, TimeScale);

export default {
  mixins: [chartClickMixin],
  components: {
    StandardLayout,
    LineChart: Line
  },
  data () {
    return {
      chartData: null,
      chartLoading: false,
      opponentUserModels: null
    };
  },
  computed: {
    chartOptions () {
      return {
        ...baseChartOptions,
        scales: {
          x: {
            type: "time",
            time: {
              unit: "month",
              displayFormats: {
                month: "MMM yyyy"
              }
            },
            title: {
              display: true,
              text: "Tournaments & Leagues"
            }
          },
          y: {
            title: {
              display: true,
              text: "Ratings"
            }
          }
        },
        plugins: {
          ...baseChartOptions.plugins,
          legend: {
            position: "bottom",
            labels: {
              generateLabels: (chart) => {
                const labels = chart.legend.legendItems || chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  fillStyle: ds.borderColor,
                  strokeStyle: ds.borderColor,
                  hidden: false,
                  index: i
                }));
                labels.forEach((label, i) => {
                  if (i === this.selectedDatasetIndex) {
                    label.fontWeight = "bold";
                    label.strokeStyle = "#21356A";
                    label.lineWidth = 2;
                  } else {
                    label.fontWeight = "normal";
                    label.strokeStyle = label.fillStyle;
                    label.lineWidth = 0;
                  }
                });
                return labels;
              }
            }
          }
        },
        onClick: (event, elements, chart) => {
          const nearest = chart.getElementsAtEventForMode(event, "nearest", { intersect: false }, false);
          this.handleChartClick(nearest);
        }
      };
    }
  },
  created () {
    this.loadChartData();
  },
  beforeUnmount () {
    if (this.opponentUserModels) {
      Object.values(this.opponentUserModels).forEach(model => {
        if (model.unsubscribe) model.unsubscribe();
      });
    }
  },
  methods: {
    async loadChartData () {
      this.chartLoading = true;
      try {
        const session = createSession(this.$store);
        const { collection, collectionGroup, query, where, getDocs } = dbFns;
        const currentUserId = this.$store.user.id;

        const [playersSnapshot, membersSnapshot] = await Promise.all([
          getDocs(query(
            collection(db, "players"),
            where("playerUserId", "==", currentUserId)
          )),
          getDocs(query(
            collectionGroup(db, "memberSnapshots"),
            where("memberUserId", "==", currentUserId)
          ))
        ]);

        if (playersSnapshot.empty && membersSnapshot.empty) {
          return;
        }

        const genericPlayerAndMemberSnapshotObjects = [];
        const tournamentIds = [];
        const raceIds = [];

        playersSnapshot.forEach(doc => {
          const data = doc.data();
          genericPlayerAndMemberSnapshotObjects.push({
            createdAt: data.createdAt,
            userId: data.playerUserId,
            mu: data.startOfTournamentMu,
            sigma: data.startOfTournamentSigma
          });
          tournamentIds.push(data.tournamentId);
        });

        membersSnapshot.forEach(doc => {
          const data = doc.data();
          genericPlayerAndMemberSnapshotObjects.push({
            createdAt: data.createdAt,
            userId: data.memberUserId,
            mu: data.startOfRaceMu,
            sigma: data.startOfRaceSigma
          });
          raceIds.push(data.raceId);
        });

        genericPlayerAndMemberSnapshotObjects.sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());

        const uniqueTournamentIds = [...new Set(tournamentIds)];
        const opponentUserIdSet = new Set();

        for (let i = 0; i < uniqueTournamentIds.length; i += 30) {
          const chunk = uniqueTournamentIds.slice(i, i + 30);
          const opponentTournamentSnapshots = await getDocs(query(
            collection(db, "players"),
            where("tournamentId", "in", chunk),
            where("playerUserId", "!=", currentUserId)
          ));
          opponentTournamentSnapshots.forEach(doc => {
            opponentUserIdSet.add(doc.data().playerUserId);
          });
        }

        const uniqueRaceIds = [...new Set(raceIds)];
        for (let i = 0; i < uniqueRaceIds.length; i += 30) {
          const chunk = uniqueRaceIds.slice(i, i + 30);
          const opponentSnapshots = await getDocs(query(
            collectionGroup(db, "memberSnapshots"),
            where("raceId", "in", chunk),
            where("memberUserId", "!=", currentUserId)
          ));
          opponentSnapshots.forEach(doc => {
            opponentUserIdSet.add(doc.data().memberUserId);
          });
        }

        const opponentDocs = [];
        const opponentUserIdArray = [...opponentUserIdSet];
        for (let i = 0; i < opponentUserIdArray.length; i += 30) {
          const chunk = opponentUserIdArray.slice(i, i + 30);
          const [tournamentPlayers, leagueMembers] = await Promise.all([
            getDocs(query(
              collection(db, "players"),
              where("playerUserId", "in", chunk)
            )),
            getDocs(query(
              collectionGroup(db, "memberSnapshots"),
              where("memberUserId", "in", chunk)
            ))
          ]);
          tournamentPlayers.forEach(doc => {
            const data = doc.data();
            opponentDocs.push({
              createdAt: data.createdAt,
              userId: data.playerUserId,
              mu: data.startOfTournamentMu,
              sigma: data.startOfTournamentSigma
            });
          });
          leagueMembers.forEach(doc => {
            const data = doc.data();
            opponentDocs.push({
              createdAt: data.createdAt,
              userId: data.memberUserId,
              mu: data.startOfRaceMu,
              sigma: data.startOfRaceSigma
            });
          });
        }

        const opponentDocsByUser = {};
        for (const doc of opponentDocs) {
          if (!opponentDocsByUser[doc.userId]) {
            opponentDocsByUser[doc.userId] = [];
          }
          opponentDocsByUser[doc.userId].push(doc);
        }

        for (const userId of Object.keys(opponentDocsByUser)) {
          opponentDocsByUser[userId].sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis());
        }

        const sixMonthsAgo = subMonths(new Date(), 6);
        const opponentUserIds = Object.keys(opponentDocsByUser).filter(userId => {
          const docs = opponentDocsByUser[userId];
          const lastCreatedAt = docs[docs.length - 1].createdAt.toDate();
          return lastCreatedAt >= sixMonthsAgo;
        });

        this.opponentUserModels = {};
        const scorecardPromises = [];

        scorecardPromises.push(this.$store.user.promiseScorecard);

        const userModels = await Promise.all(
          opponentUserIds.map(userId => session.findUserById(userId))
        );
        opponentUserIds.forEach((userId, i) => {
          this.opponentUserModels[userId] = userModels[i];
          userModels[i].scorecard;
          scorecardPromises.push(userModels[i].promiseScorecard);
        });

        await Promise.all(scorecardPromises);

        const datasets = [];
        datasets.push({
          label: this.$store.user.playerName,
          data: buildRatingDataPoints(genericPlayerAndMemberSnapshotObjects, this.$store.user.scorecard),
          borderColor: this.$vuetify.theme.current.colors.primary,
          backgroundColor: this.$vuetify.theme.current.colors.primary,
          pointRadius: 3,
          tension: 0.1,
          borderWidth: 2
        });

        const secondaryColor = this.$vuetify.theme.current.colors.secondary;
        const opponentShades = generateShades(secondaryColor, opponentUserIds.length);
        for (let i = 0; i < opponentUserIds.length; i++) {
          const userId = opponentUserIds[i];
          const docs = opponentDocsByUser[userId];
          const userModel = this.opponentUserModels[userId];
          const scorecard = userModel.scorecard;
          const dataPoints = buildRatingDataPoints(docs, scorecard);
          if (dataPoints.length > 0) {
            datasets.push({
              label: userModel.playerName || "Unknown",
              data: dataPoints,
              borderColor: opponentShades[i],
              backgroundColor: opponentShades[i],
              pointRadius: 2,
              tension: 0.1,
              borderWidth: 1.5
            });
          }
        }

        this.chartData = { datasets };
      } catch (err) {
        console.error("Failed to load chart data", err);
      } finally {
        this.chartLoading = false;
      }
    },
    applySelectedStyles () {
      const chart = this.$refs.chartComponent?.chart;
      if (!chart) return;
      const datasets = chart.data.datasets;
      const selected = this.selectedDatasetIndex;
      datasets.forEach((ds, i) => {
        ds._originalColor ??= ds.borderColor;
        if (selected === null || i !== selected) {
          ds.borderWidth = i === 0 ? 2 : 1.5;
          ds.borderColor = ds._originalColor;
          ds.backgroundColor = ds._originalColor;
          ds.pointRadius = i === 0 ? 3 : 2;
        } else {
          ds.pointRadius = 4;
          if (i === 0) {
            ds.borderWidth = 3;
          } else {
            ds.borderColor = "#21356A";
          }
        }
      });
      chart.update();
    }
  }
};
</script>
